import { useState } from 'react';

import MoveDown from '@mui/icons-material/MoveDown';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Button, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { Button as ActionButton, HttpError, useNotify, useRefresh, useUpdate } from 'react-admin';
import { DataProviderResource } from 'src/constants';
import { MoveAccountBodyDto } from 'src/dtos';
import { HttpErrorTypography } from 'src/lib';
import { BaseAccount, User } from 'src/models';
import * as yup from 'yup';

interface MoveAccountFormValues {
  phoneNumber: string;
  email: string;
}

const validationSchema = yup.object().shape({
  phoneNumber: yup.string().required('Phone Number is required'),
  email: yup.string().email('Enter a valid email').required('Email is required'),
});

export const MoveAccountButton = ({ account, user }: { account: BaseAccount; user: User }) => {
  const [isOpen, setIsOpen] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const [moveAccount, { isLoading, isError, error, reset }] = useUpdate<
    MoveAccountBodyDto & { id: string },
    HttpError
  >();

  const onMove = (values: MoveAccountFormValues) => {
    moveAccount(
      DataProviderResource.MoveAccount,
      {
        id: account.id,
        data: {
          email: values.email,
          phoneNumber: values.phoneNumber,
        },
      },
      {
        mutationMode: 'pessimistic',
        onSuccess: () => {
          notify(`Account was successfully moved.`, {
            type: 'success',
            undoable: true,
          });
          onClose();
        },
        onSettled: () => {
          refresh();
        },
      },
    );
  };

  const formik = useFormik<MoveAccountFormValues>({
    initialValues: {
      phoneNumber: user.phoneNumber,
      email: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: onMove,
  });

  const onClose = () => {
    setIsOpen(false);
    reset();
  };

  return (
    <>
      <ActionButton
        label='Move'
        onClick={() => setIsOpen(true)}
        startIcon={<MoveDown />}
        disabled={
          account.isRoot ||
          !Boolean(account.accountId) ||
          (account.type.isEntity && !account.accountNumber) ||
          !account.linkStatus.isLinked
        }
      />
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} fullWidth>
        <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
          <DialogTitle>Move Account</DialogTitle>
          <DialogContent>
            <Grid container direction='column' spacing={2}>
              <Grid item xs={6} md={6}>
                <Typography variant='subtitle1' color='textSecondary'>
                  You are about to move {account.customTypeLabel} account with account number {account.accountNumber} to
                  the new user below. The original user with email address {user.email} will continue to have access to
                  the account.
                </Typography>
              </Grid>
              <Grid item xs={6} md={6}>
                <Typography variant='subtitle2' color='textSecondary'>
                  *The action is irreversible.
                </Typography>
              </Grid>
              <Grid item xs={6} md={6}>
                <TextField
                  name='firstName'
                  label='First Name'
                  variant='standard'
                  margin='dense'
                  disabled
                  value={account.primaryAccountHolder?.firstName}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <TextField
                  name='middleName'
                  label='Middle Name'
                  variant='standard'
                  margin='dense'
                  disabled
                  value={account.primaryAccountHolder?.middleName}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <TextField
                  name='lastName'
                  label='Last Name'
                  variant='standard'
                  margin='dense'
                  disabled
                  value={account.primaryAccountHolder?.lastName}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <TextField
                  name='suffix'
                  label='Suffix'
                  variant='standard'
                  margin='dense'
                  disabled
                  value={account.primaryAccountHolder?.suffix}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <TextField
                  name='phoneNumber'
                  label='Phone Number'
                  variant='standard'
                  margin='dense'
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  error={Boolean(formik.errors.phoneNumber)}
                  helperText={formik.errors.phoneNumber}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <TextField
                  name='email'
                  label='Email'
                  variant='standard'
                  margin='dense'
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={Boolean(formik.errors.email)}
                  helperText={formik.errors.email}
                  fullWidth
                />
              </Grid>
              <Grid item md={12} sm={12}>
                <Typography variant='subtitle2' color='textSecondary'>
                  *An email will be sent to the user asking to reset the password before they can access the account.
                </Typography>
              </Grid>
              <Grid item md={12} sm={12}>
                {isError && <HttpErrorTypography error={error} />}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Grid item xs={12} container direction='row' justifyContent='flex-end' spacing={2}>
              <Grid item>
                <Button variant='outlined' color='inherit' size='medium' onClick={onClose}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button variant='contained' color='primary' size='medium' disabled={isLoading} type='submit'>
                  Move
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};
